import { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import PageHero from "../components/PageHero";
import SEOHead, { useSiteBase, imageGallerySchema, breadcrumbSchema } from "../lib/SEOHead";
import Breadcrumbs from "../components/Breadcrumbs";
import BookCallCTA from "../components/BookCallCTA";

const CATEGORIES = [
  { key: "all", label: "All Photos" },
  { key: "room-jacuzzi", label: "Jacuzzi Suite" },
  { key: "room-king", label: "King Suite" },
  { key: "room-family", label: "Family Suite" },
  { key: "room-queen-double", label: "Queen Double" },
  { key: "room-standard-queen", label: "Standard Queen" },
  { key: "room-accessible", label: "Accessible" },
  { key: "exterior", label: "Exterior" },
  { key: "pool", label: "Pool" },
  { key: "lobby", label: "Front Desk" },
];

const ALT_TEXT = {
  "room-jacuzzi": "Renovated Jacuzzi Suite with private jetted tub at Westbridge Inn & Suites in Clinton, MO",
  "room-king": "Renovated King Suite with Euro-top mattress at Westbridge Inn & Suites, Clinton, MO",
  "room-family": "Three-Bedroom Family Suite with three full-size beds at Westbridge Inn & Suites",
  "room-queen-double": "Queen Double room with two double beds at Westbridge Inn & Suites in Clinton, MO",
  "room-standard-queen": "Renovated Standard Queen guest room at Westbridge Inn & Suites",
  "room-accessible": "Accessible-layout Queen room at Westbridge Inn & Suites in Clinton, MO",
  "exterior": "Exterior of Westbridge Inn & Suites — hotel in Clinton, Missouri",
  "pool": "Seasonal outdoor pool at Westbridge Inn & Suites in Clinton, MO",
  "lobby": "Front desk and lobby at Westbridge Inn & Suites",
  "logo": "Westbridge Inn & Suites logo",
};

export default function GalleryPage() {
  const [manifest, setManifest] = useState([]);
  const [filter, setFilter] = useState("all");
  const [openIdx, setOpenIdx] = useState(-1);
  const { base } = useSiteBase();

  useEffect(() => {
    fetch("/images/manifest.json")
      .then((r) => r.json())
      .then((m) => setManifest(m.filter((x) => x.ok && x.category !== "logo")))
      .catch(() => setManifest([]));
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (openIdx < 0) return;
      if (e.key === "Escape") setOpenIdx(-1);
      if (e.key === "ArrowRight") setOpenIdx((i) => Math.min(visible.length - 1, i + 1));
      if (e.key === "ArrowLeft") setOpenIdx((i) => Math.max(0, i - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openIdx, filter, manifest]);

  const visible = filter === "all" ? manifest : manifest.filter((x) => x.category === filter);

  return (
    <>
      <PageHero
        eyebrow="Gallery"
        title="Real photos from every corner of the property."
        intro="Every image below is a real photograph of Westbridge Inn & Suites or the neighborhood around it. Zero stock, all self-hosted."
        image="/images/exterior-hero.jpg"
        metaTitle="Photo Gallery — Rooms, Pool, Exterior & Clinton MO"
        metaDesc="Real property photos of Westbridge Inn & Suites in Clinton, Missouri — renovated rooms, Jacuzzi Suite, outdoor pool, exterior, and around town."
      />
      <SEOHead
        title="Photo Gallery — Rooms, Pool, Exterior & Clinton MO"
        description="Real property photos of Westbridge Inn & Suites in Clinton, Missouri — renovated rooms, Jacuzzi Suite, outdoor pool, exterior, and around town."
        canonicalPath="/gallery"
        image="/images/exterior-hero.jpg"
        schema={manifest.length > 0 ? [
          imageGallerySchema(base, manifest.map((m) => `/images/${m.file}`)),
          breadcrumbSchema(base, [{ label: "Gallery", to: "/gallery" }]),
        ] : []}
      />
      <Breadcrumbs items={[{ label: "Gallery" }]} />

      <section className="bg-[#FDFBF7]">
        <div className="wb-container pt-6 md:pt-8 pb-16 md:pb-24">
          <div className="flex flex-wrap gap-2 mb-8" data-testid="gallery-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                onClick={() => setFilter(c.key)}
                data-testid={`gallery-filter-${c.key}`}
                className={`px-4 py-2 text-sm font-bold rounded-sm border ${
                  filter === c.key
                    ? "bg-[#E85D26] text-white border-[#E85D26]"
                    : "bg-white text-[#1A1A1A] border-[#E5DFD3] hover:border-[#E85D26]"
                }`}
                style={{ fontFamily: "'Nunito', sans-serif" }}
              >
                {c.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {visible.map((img, idx) => (
              <button
                key={img.file}
                onClick={() => setOpenIdx(idx)}
                className="relative aspect-[4/3] overflow-hidden bg-[#F5F0E6] group"
                data-testid={`gallery-thumb-${idx}`}
              >
                <img
                  src={`/images/${img.file}`}
                  alt={ALT_TEXT[img.category] || "Westbridge Inn & Suites property photo"}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors" />
              </button>
            ))}
          </div>

          {visible.length === 0 && (
            <p className="text-center py-16 text-[#4A4641]">No photos in this category yet.</p>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {openIdx >= 0 && visible[openIdx] && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setOpenIdx(-1)}
          data-testid="gallery-lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setOpenIdx(-1); }}
            className="absolute top-4 right-4 p-3 text-white/80 hover:text-white"
            aria-label="Close"
            data-testid="gallery-lightbox-close"
          >
            <X className="h-6 w-6" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setOpenIdx(Math.max(0, openIdx - 1)); }}
            className="absolute left-4 p-3 text-white/80 hover:text-white disabled:opacity-30"
            disabled={openIdx === 0}
            aria-label="Previous"
            data-testid="gallery-lightbox-prev"
          >
            <ChevronLeft className="h-8 w-8" />
          </button>
          <img
            src={`/images/${visible[openIdx].file}`}
            alt={ALT_TEXT[visible[openIdx].category] || "Westbridge property photo"}
            className="max-w-[95vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={(e) => { e.stopPropagation(); setOpenIdx(Math.min(visible.length - 1, openIdx + 1)); }}
            className="absolute right-4 p-3 text-white/80 hover:text-white disabled:opacity-30"
            disabled={openIdx === visible.length - 1}
            aria-label="Next"
            data-testid="gallery-lightbox-next"
          >
            <ChevronRight className="h-8 w-8" />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-xs">
            {openIdx + 1} / {visible.length} · Use ← → to navigate
          </div>
        </div>
      )}

      <BookCallCTA />
    </>
  );
}
